import { z } from "zod";

import { LevelSchema } from "api/services/content/schemas/exercises";
import { TargetJointsSchema } from "api/services/content/schemas/shared";
import { GroupSchema, HealthFormTypeSchema, KeySchema } from "api/services/treatment/schemas/Treatment";

const sharedCourseExerciseSchema = z.object({
  end_protocol_week: z.number().nullable(),
  id: z.number(),
  introduction_protocol_week: z.number(),
  premium: z.boolean(),
  target_joints: z.array(TargetJointsSchema),
  levels: z.array(LevelSchema),
});

const UserCourseExerciseSchema = sharedCourseExerciseSchema.merge(
  z.object({
    active: z.boolean(),
    current_level: z.number(),
  })
);

const UserCourseTrimmedExerciseSchema = sharedCourseExerciseSchema.merge(
  z.object({
    active: z.boolean(),
    current_level: z.number().optional(),
  })
);

const UserCourseTemplateExerciseSchema = sharedCourseExerciseSchema;

const UserCourseLibraryExerciseSchema = z.object({
  creator_id: z.number().nullable().optional(),
  exercise_id: z.number(),
  levels: z.array(LevelSchema),
  premium: z.boolean(),
  target_joints: z.array(TargetJointsSchema),
});

const UserCourseLibraryExercisesSchema = z.array(UserCourseLibraryExerciseSchema);

export const UserCourseLessonSchema = z.object({
  creator_id: z.number().nullable().optional(),
  id: z.number(),
  image_url: z.string().nullable(),
  parts_count: z.number(),
  title: z.string(),
  target_joints: z.array(TargetJointsSchema),
});

const UserCourseTemplateSchema = z.object({
  id: z.number(),
  name: z.string().optional().nullable(),
  standardized: z.boolean(),
  group: GroupSchema.optional().nullable(),
  key: KeySchema.optional().nullable(),
});

const UserCourseTemplatesSchema = z.array(UserCourseTemplateSchema);

// api schemas

export const GetUserCourseResponseSchema = z.object({
  exercises: z.array(UserCourseExerciseSchema),
  lessons: z.array(UserCourseLessonSchema).optional().nullable(),
  max_exercises_per_day: z.number(),
  strategy: z.string(),
  functional_test: z.boolean(),
  health_form_type: HealthFormTypeSchema,
});

export const GetUserCourseTemplateResponseSchema = z.object({
  exercises: z.array(UserCourseTemplateExerciseSchema),
  lessons: z.array(UserCourseLessonSchema).optional().nullable(),
  max_exercises_per_day: z.number(),
  name: z.string().optional().nullable(),
  standardized: z.boolean(),
  key: KeySchema.optional().nullable(),
  functional_test: z.boolean(),
  health_form_type: HealthFormTypeSchema,
});

export const PutUserCoursePayloadSchema = z.object({
  exercises: z.array(
    z.object({
      exercise_id: z.number(),
      introduction_protocol_week: z.number(),
      end_protocol_week: z.number().nullable(),
      level_recommendation: z.number().optional(),
    })
  ),
  lessons: z
    .array(
      z.object({
        lesson_id: z.number(),
      })
    )
    .optional()
    .nullable(),
  max_exercises_per_day: z.number(),
  functional_test: z.boolean(),
  health_form_type: HealthFormTypeSchema,
});

const PutUserCourseArgsSchema = z.object({
  course: PutUserCoursePayloadSchema,
  patientId: z.number(),
});

export const GetUserCourseTemplatesResponseSchema = z.array(UserCourseTemplateSchema);

const CreateUserCourseTemplatePayloadSchema = z.object({
  exercises: z.array(
    z.object({
      exercise_id: z.number(),
      introduction_protocol_week: z.number(),
      end_protocol_week: z.number().nullable(),
    })
  ),
  max_exercises_per_day: z.number(),
  name: z.string(),
  functional_test: z.boolean(),
  health_form_type: HealthFormTypeSchema,
});

const CreateUserCourseArgsSchema = z.object({
  course: CreateUserCourseTemplatePayloadSchema,
  therapistId: z.number(),
});

const UpdateUserCourseTemplatePayloadSchema = z.object({
  exercises: z.array(
    z.object({
      exercise_id: z.number(),
      introduction_protocol_week: z.number(),
      end_protocol_week: z.number().nullable(),
    })
  ),
  lessons: z
    .array(
      z.object({
        lesson_id: z.number(),
      })
    )
    .optional()
    .nullable(),
  max_exercises_per_day: z.number(),
  name: z.string().optional(),
  functional_test: z.boolean(),
  health_form_type: HealthFormTypeSchema,
});

const UpdateUserCourseArgsSchema = z.object({
  course: UpdateUserCourseTemplatePayloadSchema,
  therapistId: z.number(),
  templateId: z.number(),
});

const DeleteUserCourseArgsSchema = z.object({
  therapistId: z.number(),
  templateId: z.number(),
});

export type UserCourseExercise = z.infer<typeof UserCourseExerciseSchema>;

export type UserCourseTrimmedExercise = z.infer<typeof UserCourseTrimmedExerciseSchema>;

export type UserCourseTemplateExercise = z.infer<typeof UserCourseTemplateExerciseSchema>;

export type UserCourseLesson = z.infer<typeof UserCourseLessonSchema>;

export type UserCourseLibraryExercise = z.infer<typeof UserCourseLibraryExerciseSchema>;

export type UserCourseLibraryExercises = z.infer<typeof UserCourseLibraryExercisesSchema>;

export type UserCourseTemplate = z.infer<typeof UserCourseTemplateSchema>;

export type UserCourseTemplates = z.infer<typeof UserCourseTemplatesSchema>;

export type GetUserCourseResponse = z.infer<typeof GetUserCourseResponseSchema>;

export type FullUserCourse = GetUserCourseResponse;

export type GetUserCourseTemplateResponse = z.infer<typeof GetUserCourseTemplateResponseSchema>;

export type FullUserCourseTemplate = GetUserCourseTemplateResponse;

export type PutUserCoursePayload = z.infer<typeof PutUserCoursePayloadSchema>;

export type PutUserCourseArgs = z.infer<typeof PutUserCourseArgsSchema>;

export type GetUserCourseTemplatesResponse = z.infer<typeof GetUserCourseTemplatesResponseSchema>;

export type CreateUserCourseTemplatePayload = z.infer<typeof CreateUserCourseTemplatePayloadSchema>;

export type UpdateUserCourseTemplatePayload = z.infer<typeof UpdateUserCourseTemplatePayloadSchema>;

export type CreateUserCourseArgs = z.infer<typeof CreateUserCourseArgsSchema>;

export type UpdateUserCourseArgs = z.infer<typeof UpdateUserCourseArgsSchema>;

export type DeleteUserCourseArgs = z.infer<typeof DeleteUserCourseArgsSchema>;

export type HealthFormType = z.infer<typeof HealthFormTypeSchema>;
